import { useEffect, useState } from "react";
import type { RedactionSummary } from "@workspace/api-client-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ChevronDown, ChevronUp, Shield } from "lucide-react";

/**
 * Task #93 / Task #158 — disclosure shown after an AI request that lists
 * what the server-side redaction layer removed before sending text to
 * the AI provider. ClassTrack NEVER sends real student names to the AI;
 * each named student is replaced with an anonymous placeholder
 * ("Student A", "Student B", …) which is rehydrated to the real name
 * locally on ClassTrack's server before the response is returned. The
 * disclosure is read-only — there is no opt-in toggle.
 *
 * Collapsed by default after the first time the teacher sees it on a
 * given surface (tracked in localStorage via `surfaceKey`).
 */
export interface AiRedactionDisclosureProps {
  summary: RedactionSummary;
  /** Stable per-surface key, e.g. "polish" or "lesson-prep". */
  surfaceKey: string;
}

const SEEN_KEY_PREFIX = "ai-redaction-disclosure-seen:";

const COUNT_LABELS: Array<{
  key: keyof RedactionSummary["otherCounts"];
  singular: string;
  plural: string;
}> = [
  { key: "phones",      singular: "phone number",   plural: "phone numbers" },
  { key: "emails",      singular: "email address",  plural: "email addresses" },
  { key: "addresses",   singular: "address",        plural: "addresses" },
  { key: "dates",       singular: "date",           plural: "dates" },
  { key: "parents",     singular: "parent/carer",   plural: "parent/carer mentions" },
  { key: "diagnoses",   singular: "diagnosis",      plural: "diagnoses" },
  { key: "medications", singular: "medication",     plural: "medications" },
  { key: "persons",     singular: "named person",   plural: "named people" },
  { key: "schools",     singular: "school name",    plural: "school names" },
  { key: "ndis",        singular: "NDIS number",    plural: "NDIS numbers" },
  { key: "medicare",    singular: "Medicare number",plural: "Medicare numbers" },
];

function summariseCounts(counts: RedactionSummary["otherCounts"]): string[] {
  const out: string[] = [];
  for (const { key, singular, plural } of COUNT_LABELS) {
    const n = counts[key];
    if (n > 0) out.push(`${n} ${n === 1 ? singular : plural}`);
  }
  return out;
}

export function AiRedactionDisclosure({
  summary,
  surfaceKey,
}: AiRedactionDisclosureProps) {
  const seenKey = SEEN_KEY_PREFIX + surfaceKey;

  // Default: expanded the first time we render on this surface, then
  // collapsed on subsequent loads.
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return typeof window !== "undefined"
        ? window.localStorage.getItem(seenKey) === null
        : true;
    } catch {
      return true;
    }
  });
  const [whyOpen, setWhyOpen] = useState(false);

  const studentCount = summary.students.length;
  const countSummary = summariseCounts(summary.otherCounts);
  const nothingRemoved = studentCount === 0 && countSummary.length === 0;

  // Mark the surface as "seen" only when we're actually rendering the
  // disclosure — otherwise an empty redaction summary (nothing to
  // disclose) would silently consume the first-view default-expanded
  // window and the next, meaningful disclosure would start collapsed.
  useEffect(() => {
    if (nothingRemoved) return;
    try {
      window.localStorage.setItem(seenKey, "1");
    } catch {
      // localStorage may be unavailable (privacy mode) — that's fine,
      // we just lose the collapsed-by-default behaviour.
    }
  }, [seenKey, nothingRemoved]);

  if (nothingRemoved) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 text-xs">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-100 rounded-lg"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-1.5 text-gray-600 font-medium">
          <Shield className="h-3 w-3 text-gray-400" />
          Removed before sending to AI
          {(studentCount > 0 || countSummary.length > 0) && (
            <span className="text-gray-400 font-normal">
              ({studentCount > 0 && `${studentCount} ${studentCount === 1 ? "name" : "names"}`}
              {studentCount > 0 && countSummary.length > 0 && " · "}
              {countSummary.slice(0, 2).join(", ")}
              {countSummary.length > 2 && "…"}
              )
            </span>
          )}
        </span>
        {expanded ? (
          <ChevronUp className="h-3 w-3 text-gray-400" />
        ) : (
          <ChevronDown className="h-3 w-3 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-gray-200 pt-2.5">
          {summary.students.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                Student names
              </div>
              <ul className="space-y-1">
                {summary.students.map((s) => (
                  <li
                    key={s.studentId}
                    className="flex items-center justify-between gap-3 bg-white border border-gray-100 rounded-md px-2.5 py-1.5"
                    data-testid={`redaction-student-${s.studentId}`}
                  >
                    <span className="text-gray-700">
                      <span className="font-mono text-[11px] text-gray-500">
                        {s.placeholder || "Student ?"}
                      </span>
                      <span className="text-gray-400"> = </span>
                      <span className="font-medium">{s.originalName}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {countSummary.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                Other items removed
              </div>
              <p className="text-gray-600">{countSummary.join(", ")}</p>
              <p className="text-[11px] text-gray-400">
                These categories are always removed before AI sees your notes.
              </p>
            </div>
          )}

          <div className="pt-1 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setWhyOpen(true)}
              className="text-[11px] text-primary hover:underline"
            >
              Why?
            </button>
            <a
              href="/ai-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-primary hover:underline"
            >
              Learn more
            </a>
          </div>
        </div>
      )}

      <Dialog open={whyOpen} onOpenChange={setWhyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Why ClassTrack removes this</DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed text-gray-600">
              ClassTrack strips student names, phone numbers, addresses,
              diagnoses, medications, parent/carer names and similar
              identifiers from your notes before sending them to the AI
              provider. The AI sees pseudonymous placeholders ("Student A",
              "[phone]", "[diagnosis]"), not real identifiers. Under
              OpenAI's API data policy, prompts sent via the API are not
              used to train their models. The polished result is mapped
              back to real names locally on ClassTrack's server.
              Registered student names are replaced before text is sent
              to the AI provider, and there is no opt-in for sending
              real identifiers.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
